import type { Reference } from "@apollo/client";
import type { relayStylePagination } from "@apollo/client/utilities";

type RelayPolicy = ReturnType<typeof relayStylePagination>;
type RelayMerge = Exclude<NonNullable<RelayPolicy["merge"]>, boolean>;

// A refetch has no `after`, so relayStylePagination replaces the connection with
// the first page and drops everything loaded with fetchMore. Keep those older
// pages for as long as the refetched page still overlaps them. The query needs
// `refetchWritePolicy: "merge"`, or Apollo hides `existing` from merge on refetch.
export function keepLoadedPages(policy: RelayPolicy): RelayPolicy {
  const relayMerge = policy.merge as RelayMerge;

  return {
    ...policy,
    merge(existing, incoming, options) {
      const merged = relayMerge(existing, incoming, options);

      if (options.args?.after || !merged?.edges.length || !existing?.pageInfo) {
        return merged;
      }

      const nodeRef = (edge: unknown) =>
        options.readField<Reference>("node", edge as Reference)?.__ref;
      const lastNodeRef = nodeRef(merged.edges[merged.edges.length - 1]);
      const overlap = existing.edges.findIndex(
        (edge) => nodeRef(edge) === lastNodeRef,
      );

      if (overlap === -1) {
        return merged;
      }

      return {
        ...merged,
        edges: [...merged.edges, ...existing.edges.slice(overlap + 1)],
        pageInfo: {
          ...merged.pageInfo,
          hasNextPage: existing.pageInfo.hasNextPage,
          endCursor: existing.pageInfo.endCursor,
        },
      };
    },
  };
}
